import cuid from 'cuid'
import * as LaunchDarkly from 'ldclient-js'

import { loadUserOverrides, loadFeatures, TogglesState } from 'common/feature-togglings/reducer'

import {
    getItem,
    setItem,
    StoredFeatureOverrides,
    TOGGLE_OVERRIDES,
    USER_ID,
} from 'common/feature-togglings/ClientABTesting/local-storage'
import { Features } from 'common/features'

// TODO Figure out the universal part of this, we can't reference client code
import { readLdAllFlagsResult } from '@etrigan/feature-toggles/dist/launch-darkly/result-parser'
import { StringAnyMap } from '@etrigan/feature-toggles/dist/universal'
import { Store } from 'common/store'

const updateStoredUserOverrides = (state: { toggles: TogglesState }) => {
    const userOverrides: StoredFeatureOverrides = {}

    Object.keys(state.toggles.userOverrides).forEach(key => {
        const feature = key as Features
        const overrideValue = state.toggles.userOverrides[feature]
        const serverState = state.toggles.featureState[feature]
        if (overrideValue === undefined || serverState === undefined) {
            return
        }
        userOverrides[feature] = {
            serverState: serverState.enabled,
            overrideValue,
        }
    })
    setItem(TOGGLE_OVERRIDES, userOverrides)
}

// store subscriber
const createTogglesSubscriber = () => {
    let previous: TogglesState['userOverrides']
    return (state: { toggles: TogglesState }) => {
        if (state.toggles && previous !== state.toggles.userOverrides) {
            updateStoredUserOverrides(state)
            previous = state.toggles.userOverrides
        }
    }
}

let launchDarklySingleton: Promise<LaunchDarkly.LDClient | undefined>

const getLaunchDarklyClient = (launchDarklyClientId: string, launchDarklyClientTimeout: number) => {
    if (launchDarklySingleton) {
        return launchDarklySingleton
    }
    if (!launchDarklyClientId) {
        return Promise.resolve(undefined)
    }

    const resolveClient = new Promise<LaunchDarkly.LDClient>(resolve => {
        let userId: string | null = null
        try {
            userId = getItem(USER_ID, null)
        } catch (e) {}

        if (!userId) {
            userId = cuid()
            // Need to try/catch for when we have no quota
            try {
                setItem(USER_ID, userId)
            } catch (e) {}
        }

        const ldClient = LaunchDarkly.initialize(launchDarklyClientId, {
            key: userId,
            anonymous: true,
        })

        ldClient.waitForInitialization().then(() => resolve(ldClient), () => resolve())
    })
    const timeout = new Promise<undefined>(resolve => {
        setTimeout(resolve, Number(launchDarklyClientTimeout || 2000))
    })
    launchDarklySingleton = Promise.race([resolveClient, timeout])
    return launchDarklySingleton
}

export const loadUserToggleOverrides = (store: Store) => {
    const currentFeatures = store.getState().toggles.featureState
    const savedOverrideState = getItem(TOGGLE_OVERRIDES, {})

    // Find any overrides where the default state has changed
    const invalidOverrides = Object.keys(savedOverrideState).filter(key => {
        const feature = key as Features
        const savedState = savedOverrideState[feature]
        const currentState = currentFeatures[feature]

        if (!savedState || !currentState) {
            return false
        }

        return savedState.serverState !== currentState.enabled
    }) as Features[]

    invalidOverrides.forEach(io => {
        delete savedOverrideState[io]
    })
    const overrideKeys = Object.keys(savedOverrideState) as Features[]
    if (overrideKeys.length === 0) {
        return
    }

    const overrides = overrideKeys.reduce<TogglesState['userOverrides']>((acc, key) => {
        const overrideValue = savedOverrideState[key]
        if (overrideValue) {
            acc[key] = overrideValue.overrideValue
        }
        return acc
    }, {})

    store.dispatch(loadUserOverrides(overrides))
    updateStoredUserOverrides(store.getState())
}

export const initLaunchDarklyClientToggles = async (
    store: Store,
    launchDarklyClientId?: string,
    launchDarklyClientTimeout = 2000
) => {
    if (!launchDarklyClientId) {
        return
    }

    // If we are not using launch darkly the client toggles will be the same as server
    const ldClient = await getLaunchDarklyClient(launchDarklyClientId, launchDarklyClientTimeout)

    if (ldClient) {
        const allFlags: StringAnyMap = ldClient.allFlags()
        const LDClientToggleState = readLdAllFlagsResult(allFlags)
        const userAvailableToggles = allFlags['user-available-toggles']
        const overridable: Features[] =
            typeof userAvailableToggles === 'string'
                ? (userAvailableToggles.split(',') as Features[])
                : []

        // Override client features with results from launch darkly
        store.dispatch(loadFeatures(LDClientToggleState, overridable))
    }

    loadUserToggleOverrides(store)

    // Now we are all setup, start listening to changes
    const subscriber = createTogglesSubscriber()
    store.subscribe(() => subscriber(store.getState()))
}

/**
 * Send a "Track" event. Event can be tied to goals in A/B tests.
 * https://docs.launchdarkly.com/docs/running-ab-tests
 * https://docs.launchdarkly.com/docs/node-sdk-reference#section-track
 *
 * @param event Custom event name
 * @param data Optional JSON formated data related to the event
 */
export function sendLaunchDarklyEvent(event: string, data?: { [key: string]: any }) {
    ;(async () => {
        const ldClient = await launchDarklySingleton
        if (!ldClient) return
        ldClient.track(event, data)
    })()
}
